import { useState } from "react";
import { Highlight, type PrismTheme } from "prism-react-renderer";
import styles from "./CodeBlock.module.css";

type Tab = {
  label: string;
  code: string;
  language?: string;
};

type Props =
  | { title?: string; children: string; language?: string; tabs?: undefined }
  | { title?: undefined; children?: undefined; language?: undefined; tabs: Tab[] };

const kiraCodeTheme: PrismTheme = {
  plain: {
    color: "var(--code-text)",
    backgroundColor: "transparent",
  },
  styles: [
    {
      types: ["comment", "prolog", "doctype", "cdata"],
      style: { color: "var(--text-faint)", fontStyle: "italic" },
    },
    { types: ["punctuation"], style: { color: "var(--text-muted)" } },
    {
      types: ["property", "tag", "attr-name", "deleted", "parameter"],
      style: { color: "var(--rule-nitro-1)" },
    },
    {
      types: ["boolean", "number", "constant", "symbol"],
      style: { color: "var(--rule-nitro-2)" },
    },
    {
      types: ["selector", "string", "char", "builtin", "inserted", "attr-value", "regex"],
      style: { color: "var(--rule-role)" },
    },
    { types: ["operator", "entity", "url", "variable"], style: { color: "var(--text)" } },
    { types: ["atrule", "keyword", "important"], style: { color: "var(--rule-custom)" } },
    { types: ["function", "class-name", "maybe-class-name"], style: { color: "var(--rule-nitro-3)" } },
  ],
};

function HighlightedCode({ code, language }: { code: string; language: string }) {
  return (
    <Highlight theme={kiraCodeTheme} code={code} language={language}>
      {({ className, style, tokens, getLineProps, getTokenProps }) => (
        <code className={className} style={style}>
          {tokens.map((line, lineIndex) => (
            <div key={lineIndex} {...getLineProps({ line })}>
              {line.map((token, tokenIndex) => (
                <span key={tokenIndex} {...getTokenProps({ token })} />
              ))}
            </div>
          ))}
        </code>
      )}
    </Highlight>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <button type="button" className={styles.copy} onClick={handleCopy} aria-label="Copy code">
      {copied ? (
        <svg viewBox="0 0 16 16" fill="none">
          <path d="M3 8.5 6.2 12 13 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        <svg viewBox="0 0 16 16" fill="none">
          <rect x="5.5" y="5.5" width="8" height="8" rx="1.4" stroke="currentColor" strokeWidth="1.4" />
          <path d="M3.5 10.5h-1a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v1" stroke="currentColor" strokeWidth="1.4" />
        </svg>
      )}
    </button>
  );
}

export default function CodeBlock(props: Props) {
  const [active, setActive] = useState(0);

  if (props.tabs) {
    const { tabs } = props;
    return (
      <div className={styles.block}>
        <div className={styles.header}>
          <span className={styles.dots}>
            <i /> <i /> <i />
          </span>
          <div className={styles.tabs} role="tablist">
            {tabs.map((tab, i) => (
              <button
                key={tab.label}
                role="tab"
                aria-selected={i === active}
                className={`${styles.tab} ${i === active ? styles.tabActive : ""}`}
                onClick={() => setActive(i)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        <div className={styles.body}>
          <CopyButton text={tabs[active].code.trim()} />
          <pre className={styles.pre}>
            <HighlightedCode
              code={tabs[active].code.trim()}
              language={tabs[active].language ?? "tsx"}
            />
          </pre>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.block}>
      {props.title && (
        <div className={styles.header}>
          <span className={styles.dots}>
            <i /> <i /> <i />
          </span>
          <span className={styles.title}>{props.title}</span>
        </div>
      )}
      <div className={styles.body}>
        <CopyButton text={props.children.trim()} />
        <pre className={styles.pre}>
          <HighlightedCode code={props.children.trim()} language={props.language ?? "tsx"} />
        </pre>
      </div>
    </div>
  );
}
